/**
 * The census board's navigation state: which record the working pane holds,
 * and — when the board is too narrow to show both panes — which pane is on
 * screen.
 *
 * Pure and separate from the board because it is the part that has to be
 * RIGHT: the board is mounted at three widths, the narrow one drills down, and
 * a wrong transition there means a tap that appears to do nothing. Keeping it
 * a plain reducer is also what makes it testable — an app-side test cannot
 * render an app component (frontend/tsconfig.json explains why), but it can
 * exercise this.
 *
 * `level` is only consulted by the narrow layout (`.census-board[data-level]`,
 * frontend/src/index.css); the wide layout shows both panes and ignores it.
 * That is why `back` moves the level without clearing the selection — going
 * back to the list must not silently change what the wide layout would show,
 * and the row the nurse came from stays marked.
 */

/** Which pane the narrow (drill-down) layout is showing. */
export type CensusLevel = 'list' | 'work'

/** What the working pane holds. Registering is a pane, not an inline form:
 *  it is the whole job when no patient is selected. */
export type CensusWorkPane = { kind: 'register' } | { kind: 'detail'; token: string }

export interface CensusNavState {
  /** The patient whose record the working pane shows, if any. */
  selectedToken: string | null
  /** Set while the nurse is registering — outranks a selection, so "+ add"
   *  works even with a patient open. */
  registerOpen: boolean
  level: CensusLevel
}

/** Nothing selected, nothing being registered, list-first on a narrow board.
 *  On a wide board this still yields a working pane — the register flow (the
 *  only thing a nurse can do with no patient selected). */
export const INITIAL_CENSUS_NAV: CensusNavState = {
  selectedToken: null,
  registerOpen: false,
  level: 'list',
}

export type CensusNavEvent =
  | { type: 'select'; token: string }
  | { type: 'register' }
  /**
   * Registration succeeded. The flow STAYS in registration, ready for the next
   * patient — shift-start entry is ten in a row, and bouncing to the new
   * record after each one means reopening the form ten times. The saved
   * patient appears in the census list beside the form (the register mutation
   * writes them into the list cache), so nothing is lost by not navigating.
   */
  | { type: 'registered'; token: string }
  /** "Fertig" — the explicit, only exit from the entry loop. */
  | { type: 'doneRegistering' }
  | { type: 'back' }

export function censusNavReducer(state: CensusNavState, event: CensusNavEvent): CensusNavState {
  switch (event.type) {
    case 'select':
      return { selectedToken: event.token, registerOpen: false, level: 'work' }
    case 'register':
      return { selectedToken: null, registerOpen: true, level: 'work' }
    case 'registered':
      // Remember who was last saved WITHOUT leaving the form: `registerOpen`
      // outranks `selectedToken` in censusWorkPane, so the pane stays on
      // registration and the token is simply what "Fertig" lands on.
      return { ...state, selectedToken: event.token, registerOpen: true, level: 'work' }
    case 'doneRegistering':
      // Out to the census she just built. On a narrow board that IS the list;
      // on a wide one the list is already visible, so the working pane shows
      // the last patient entered — useful (admit / place them next), and never
      // a void.
      return { ...state, registerOpen: false, level: 'list' }
    case 'back':
      return { ...state, level: 'list' }
  }
}

/**
 * What the working pane renders. With no patient selected there is exactly one
 * thing to do on this surface — register someone — so that is the pane, rather
 * than a statistics tile that offers no action.
 *
 * `registerOpen` outranks a selection, which is what keeps the entry loop
 * looping: a successful save records the new token but leaves the pane on
 * registration until the nurse says she is finished.
 */
export function censusWorkPane(state: CensusNavState): CensusWorkPane {
  if (state.registerOpen || !state.selectedToken) return { kind: 'register' }
  return { kind: 'detail', token: state.selectedToken }
}
